import { useMemo } from "react";
import dayjs from "dayjs";

import { useAccountData } from "../firebase";
import { getUrlSearchParams } from "../otherHelpers";
import { isVendorSurveyYearIncomplete } from "./vendors";

const MAX_ENTRIES = 5;

const findLatestStatus = (surveyData) => {
  if (!Array.isArray(surveyData) || surveyData.length === 0) {
    return null;
  }
  return surveyData[surveyData.length - 1]?.status || null;
};

const buildSummary = (customers) => {
  const buckets = (customers || []).reduce(
    (acc, { surveyData }) => {
      const latest = findLatestStatus(surveyData);
      if (latest === "requested") {
        return { ...acc, pendingIncoming: acc.pendingIncoming + 1 };
      }
      if (latest === "pending" || latest === "unconfirmed") {
        return { ...acc, pendingShare: acc.pendingShare + 1 };
      }
      if (latest === "accepted") {
        return { ...acc, accepted: acc.accepted + 1 };
      }
      if (latest === "revoked") {
        return { ...acc, revoked: acc.revoked + 1 };
      }
      return acc;
    },
    { pendingIncoming: 0, pendingShare: 0, accepted: 0, revoked: 0 }
  );
  const legacyInvitedCount = (customers || []).filter(
    ({ surveyData, survey }) =>
      !(Array.isArray(surveyData) && surveyData.length > 0) &&
      survey?.status === "invited"
  ).length;
  return {
    totalCount: (customers || []).length,
    pendingIncomingCount: buckets.pendingIncoming,
    pendingOutgoingShareCount: buckets.pendingShare,
    acceptedCount: buckets.accepted,
    revokedCount: buckets.revoked,
    legacyInvitedCount
  };
};

const synthesizeLegacyRequest = (customer) => {
  const legacy = customer.survey;
  if (Array.isArray(customer.surveyData) && customer.surveyData.length > 0) {
    return null;
  }
  if (!legacy || legacy.status !== "invited") {
    return null;
  }
  const year = Number(legacy.year) || dayjs().year() - 1;
  return {
    relationshipId: customer.id,
    buyerId: customer.buyerId,
    buyerName: customer.buyerName,
    year,
    status: "requested"
  };
};

export const buildPendingRequests = (customers, vendorSurveys) => {
  const completedYears = new Set(
    (vendorSurveys || [])
      .filter(({ tonsCo2ePerDollar }) => tonsCo2ePerDollar !== undefined)
      .filter(({ year }) => !isVendorSurveyYearIncomplete(vendorSurveys, year))
      .map(({ year }) => Number(year))
  );
  const fromSurveyData = (customers || []).flatMap((customer) =>
    (customer.surveyData || [])
      .filter(({ status }) => status === "requested")
      .map(({ year }) => ({
        relationshipId: customer.id,
        buyerId: customer.buyerId,
        buyerName: customer.buyerName,
        year: Number(year),
        status: "requested"
      }))
  );
  const fromLegacy = (customers || [])
    .map(synthesizeLegacyRequest)
    .filter(Boolean);
  return [...fromSurveyData, ...fromLegacy]
    .map((entry) => ({
      ...entry,
      hasCompletedSurveyForYear: completedYears.has(entry.year)
    }))
    .slice(0, MAX_ENTRIES);
};

const buildPendingShares = (customers) =>
  (customers || [])
    .flatMap((customer) =>
      (customer.surveyData || [])
        .filter(
          ({ status }) => status === "pending" || status === "unconfirmed"
        )
        .map(({ year }) => ({
          relationshipId: customer.id,
          buyerId: customer.buyerId,
          buyerName: customer.buyerName,
          year: Number(year)
        }))
    )
    .slice(0, MAX_ENTRIES);

const resolveFocusedRelationshipId = (customers) => {
  const relationshipId = getUrlSearchParams("relationshipId");
  if (relationshipId) {
    return relationshipId;
  }
  const selectedId = getUrlSearchParams("selectedId");
  if (!selectedId) {
    return null;
  }
  const match = (customers || []).find(({ buyerId }) => buyerId === selectedId);
  return match?.id || null;
};

export const useCustomersTabContext = (customers) => {
  const [accountData] = useAccountData();

  return useMemo(() => {
    const vendorSurveys = accountData?.vendorSurveys || [];
    const availableYears = vendorSurveys
      .map(({ year }) => Number(year))
      .sort((a, b) => b - a);
    const focusedRelationshipId = resolveFocusedRelationshipId(customers);
    const focusedCustomer = focusedRelationshipId
      ? (customers || []).find(({ id }) => id === focusedRelationshipId)
      : null;
    return {
      customersSummary: buildSummary(customers),
      pendingRequests: buildPendingRequests(customers, vendorSurveys),
      pendingShares: buildPendingShares(customers),
      availableYears,
      focusedRelationshipId,
      focusedBuyerName: focusedCustomer?.buyerName || null
    };
  }, [customers, accountData]);
};
